/* ─── Portal: Event Routes — Read-only client view ─────────── */

type PlaceType = {
  value: string;
  label: string;
  icon: string;
  color: string;
  bg: string;
  border: string;
};

const PLACE_TYPES: PlaceType[] = [
  { value: "home",       label: "Home",         icon: "ri-home-4-line",       color: "text-amber-600",   bg: "bg-amber-50",     border: "border-amber-200" },
  { value: "session",    label: "Session",      icon: "ri-camera-3-line",     color: "text-sky-600",     bg: "bg-sky-50",       border: "border-sky-200" },
  { value: "church",     label: "Church",       icon: "ri-building-4-line",   color: "text-violet-600",  bg: "bg-violet-50",    border: "border-violet-200" },
  { value: "venue",      label: "Venue",        icon: "ri-goblet-line",       color: "text-[#a6853e]",   bg: "bg-[#C8A96E]/10", border: "border-[#C8A96E]/40" },
  { value: "garden",     label: "Garden",       icon: "ri-plant-line",        color: "text-emerald-600", bg: "bg-emerald-50",   border: "border-emerald-200" },
  { value: "beach",      label: "Beach",        icon: "ri-landscape-line",    color: "text-cyan-600",    bg: "bg-cyan-50",      border: "border-cyan-200" },
  { value: "hotel",      label: "Hotel",        icon: "ri-hotel-line",        color: "text-rose-600",    bg: "bg-rose-50",      border: "border-rose-200" },
  { value: "restaurant", label: "Restaurant",   icon: "ri-restaurant-line",   color: "text-orange-600",  bg: "bg-orange-50",    border: "border-orange-200" },
  { value: "studio",     label: "Studio",       icon: "ri-ancient-gate-line", color: "text-slate-600",   bg: "bg-slate-100",    border: "border-slate-300" },
  { value: "park",       label: "Park",         icon: "ri-map-pin-2-line",    color: "text-lime-600",    bg: "bg-lime-50",      border: "border-lime-200" },
  { value: "transport",  label: "Transport",    icon: "ri-car-line",          color: "text-slate-500",   bg: "bg-slate-100",    border: "border-slate-200" },
  { value: "other",      label: "Location",     icon: "ri-map-pin-line",      color: "text-slate-500",   bg: "bg-slate-50",     border: "border-slate-200" },
];

function getPlaceType(value: string): PlaceType {
  return PLACE_TYPES.find((p) => p.value === value) ?? PLACE_TYPES[PLACE_TYPES.length - 1];
}

/* ─── Types ─────────────────────────────────────────────────── */
export type PortalRouteStop = {
  id: string;
  route_id: string;
  label: string;
  address: string;
  place_type: string;
  time_scheduled: string | null;
  duration_min: number | null;
  notes: string | null;
  sort_order: number;
};

export type PortalEventRoute = {
  id: string;
  title: string;
  event_date: string | null;
  notes: string | null;
  stops: PortalRouteStop[];
};

/* ─── Helpers ───────────────────────────────────────────────── */
function buildGoogleMapsUrl(stops: PortalRouteStop[]): string {
  const addrs = stops.map((s) => encodeURIComponent(s.address));
  if (addrs.length === 0) return "https://maps.google.com";
  if (addrs.length === 1) return `https://www.google.com/maps/search/?api=1&query=${addrs[0]}`;
  const origin = addrs[0];
  const destination = addrs[addrs.length - 1];
  const waypoints = addrs.slice(1, -1).join("|");
  return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints ? `&waypoints=${waypoints}` : ""}`;
}

function buildWazeUrl(stop: PortalRouteStop): string {
  return `https://waze.com/ul?q=${encodeURIComponent(stop.address)}&navigate=yes`;
}

function fmtEventDate(d: string | null): string {
  if (!d) return "";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function fmtDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  return `${m}min`;
}

/* ─── Single route view ─────────────────────────────────────── */
interface RouteViewProps {
  route: PortalEventRoute;
  brandColor: string;
}

function RouteView({ route, brandColor }: RouteViewProps) {
  const stops = route.stops;
  const totalMinutes = stops.reduce((acc, s) => acc + (s.duration_min ?? 0), 0);

  if (stops.length === 0) return null;

  return (
    <div className="space-y-4">
      {/* Route header */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
            style={{ backgroundColor: `${brandColor}18` }}
          >
            <i className="ri-route-line text-lg" style={{ color: brandColor }}></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[#0f172a] font-bold text-sm">{route.title}</h3>
            {route.event_date && (
              <p className="text-slate-400 text-xs mt-0.5 flex items-center gap-1.5">
                <i className="ri-calendar-event-line text-xs" style={{ color: brandColor }}></i>
                {fmtEventDate(route.event_date)}
              </p>
            )}
            {route.notes && (
              <p className="text-slate-500 text-xs mt-1 leading-relaxed">{route.notes}</p>
            )}
          </div>
        </div>

        {/* Stats + full route button */}
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-4 flex-wrap">
          <span className="text-xs text-slate-500 flex items-center gap-1.5">
            <i className="ri-map-pin-2-line text-xs" style={{ color: brandColor }}></i>
            <strong className="text-[#0f172a]">{stops.length}</strong> stops
          </span>
          {totalMinutes > 0 && (
            <span className="text-xs text-slate-500 flex items-center gap-1.5">
              <i className="ri-time-line text-xs" style={{ color: brandColor }}></i>
              <strong className="text-[#0f172a]">{fmtDuration(totalMinutes)}</strong> total
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <a
              href={buildGoogleMapsUrl(stops)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-map-2-line text-xs"></i>
              Full Route
            </a>
          </div>
        </div>
      </div>

      {/* Timeline of stops */}
      <div className="space-y-0">
        {stops.map((stop, idx) => {
          const pt = getPlaceType(stop.place_type);
          const isLast = idx === stops.length - 1;
          return (
            <div key={stop.id} className="flex gap-3">
              {/* Connector line + node */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div
                  className={`w-9 h-9 flex items-center justify-center rounded-xl border-2 flex-shrink-0 relative z-10 ${pt.bg} ${pt.border}`}
                >
                  <i className={`${pt.icon} ${pt.color} text-sm`}></i>
                </div>
                {!isLast && (
                  <div className="w-0.5 flex-1 bg-slate-200 my-1" style={{ minHeight: "20px" }}></div>
                )}
              </div>

              {/* Stop card */}
              <div
                className="flex-1 bg-white rounded-xl border border-slate-200 p-3.5"
                style={{ marginBottom: isLast ? 0 : "6px" }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {/* Time + label + type badge */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {stop.time_scheduled && (
                        <span
                          className="text-xs font-bold font-mono px-2 py-0.5 rounded-md whitespace-nowrap"
                          style={{ backgroundColor: `${brandColor}18`, color: brandColor }}
                        >
                          {stop.time_scheduled}
                        </span>
                      )}
                      <p className="text-[#0f172a] font-semibold text-sm">{stop.label}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${pt.bg} ${pt.color} border ${pt.border}`}>
                        {pt.label}
                      </span>
                    </div>

                    {/* Address */}
                    <p className="text-slate-500 text-xs flex items-start gap-1.5 mb-1.5">
                      <i className="ri-map-pin-line text-slate-300 text-xs mt-0.5 flex-shrink-0"></i>
                      {stop.address}
                    </p>

                    {/* Duration + notes */}
                    <div className="flex items-center gap-3 flex-wrap">
                      {stop.duration_min != null && stop.duration_min > 0 && (
                        <span className="text-slate-400 text-xs flex items-center gap-1">
                          <i className="ri-time-line text-slate-300 text-xs"></i>
                          {fmtDuration(stop.duration_min)}
                        </span>
                      )}
                      {stop.notes && (
                        <span className="text-slate-400 text-xs flex items-center gap-1">
                          <i className="ri-information-line text-slate-300 text-xs"></i>
                          {stop.notes}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Nav buttons */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stop.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-all cursor-pointer whitespace-nowrap"
                      title="Google Maps"
                    >
                      <i className="ri-map-2-line text-xs"></i>
                      Maps
                    </a>
                    <a
                      href={buildWazeUrl(stop)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1.5 rounded-lg bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100 transition-all cursor-pointer whitespace-nowrap"
                      title="Waze"
                    >
                      <i className="ri-navigation-line text-xs"></i>
                      Waze
                    </a>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Main export ───────────────────────────────────────────── */
interface EventRoutesSectionProps {
  routes: PortalEventRoute[];
  brandColor: string;
}

export default function EventRoutesSection({ routes, brandColor }: EventRoutesSectionProps) {
  const visibleRoutes = routes.filter((r) => r.stops.length > 0);

  if (visibleRoutes.length === 0) return null;

  return (
    <section>
      <h2 className="text-[#0f172a] font-bold text-base flex items-center gap-2 mb-4">
        <span className="w-5 h-5 flex items-center justify-center" style={{ color: brandColor }}>
          <i className="ri-route-line"></i>
        </span>
        Event Route{visibleRoutes.length > 1 ? "s" : ""}
      </h2>

      <div className="space-y-6">
        {visibleRoutes.map((route) => (
          <RouteView key={route.id} route={route} brandColor={brandColor} />
        ))}
      </div>
    </section>
  );
}